import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "../auth/authConfig";

export type Environment = "dev" | "stg" | "preprod" | "prod";

const FORECAST_SOURCE_BY_ENVIRONMENT: Record<Environment, string> = {
  dev: "Dev",
  stg: "Stage",
  preprod: "Preprod",
  prod: "Forecast",
};

const normalizeEnvironment = (environment?: string): Environment | undefined => {
  if (!environment) {
    return undefined;
  }

  const normalized = environment.trim().toLowerCase();

  if (normalized === "preprod") {
    return "preprod";
  }

  if (normalized === "dev" || normalized === "stg" || normalized === "prod") {
    return normalized;
  }

  return undefined;
};

export const getForecastSourceByEnvironment = (environment?: string): string => {
  const normalizedEnvironment = normalizeEnvironment(environment);
  if (!normalizedEnvironment) {
    return FORECAST_SOURCE_BY_ENVIRONMENT.prod;
  }

  return FORECAST_SOURCE_BY_ENVIRONMENT[normalizedEnvironment];
};

interface AppConfig {
  auth: {
    clientId: string;
    authority: string;
    knownAuthorities: string[];
    scopes: string[];
    redirectUri: string;
    postLogoutRedirectUri: string;
  };
  environment?: Environment;
  [key: string]: unknown;
}

let response: AppConfig | null = null;

const getEnvValue = (name: string): string => {
  // Use Vite's import.meta.env for VITE_ prefixed vars, fallback to REACT_APP_ pattern
  const viteKey = name.replace("REACT_APP_", "VITE_");
  return (
    (import.meta.env[viteKey] as string) ||
    (import.meta.env[name] as string) ||
    ""
  );
};

const loadConfigFromEnv = (): AppConfig => {
  // Helper to parse comma-separated strings or arrays from env vars
  const parseArrayEnv = (value: string): string[] => {
    if (!value) return [];
    // If it's already a JSON array string, parse it
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // Not JSON, treat as comma-separated string
    }
    // Split by comma and trim each value
    return value
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  };

  const envValue = normalizeEnvironment(getEnvValue("REACT_APP_ENVIRONMENT"));

  return {
    auth: {
      clientId: getEnvValue("REACT_APP_AZURE_CLIENT_ID") || "",
      authority: getEnvValue("REACT_APP_UI_LOGIN_AUTHORITY") || "",
      scopes: parseArrayEnv(getEnvValue("REACT_APP_UI_LOGIN_SCOPE")),
      knownAuthorities: parseArrayEnv(
        getEnvValue("REACT_APP_UI_KNOWN_AUTHORITIES"),
      ),
      redirectUri:
        getEnvValue("REACT_APP_AZURE_REDIRECT_URI") || window.location.origin,
      postLogoutRedirectUri:
        getEnvValue("REACT_APP_AZURE_POST_LOGOUT_REDIRECT_URI") || "",
    },
    applicationInsights: {
      connectionString:
        getEnvValue("APPLICATION_INSIGHTS_CONNECTION_STRING") || "",
    },
    environment: envValue,
    version: getEnvValue("REACT_APP_VERSION") || "Develop",
  };
};

const getConfig = async (): Promise<AppConfig> => {
  try {
    if (!response) {
      try {
        const configResponse = await fetch("/config.json");

        // Check if the response is ok (status 200-299)
        if (!configResponse.ok) {
          throw new Error(
            `Failed to fetch config.json: ${configResponse.status} ${configResponse.statusText}`,
          );
        }

        const rawConfig = await configResponse.json();
        const rawVersion =
          typeof rawConfig.version === "string" ? rawConfig.version.trim() : "";
        const envVersion = getEnvValue("REACT_APP_VERSION").trim();
        const version =
          rawVersion && !rawVersion.includes("#{")
            ? rawVersion
            : envVersion || "Develop";

        // Check if the config has valid auth data (not just placeholders)
        const hasValidAuth =
          rawConfig.auth?.clientId &&
          !rawConfig.auth.clientId.includes("#{") &&
          rawConfig.auth?.authority &&
          !rawConfig.auth.authority.includes("#{");

        if (!hasValidAuth) {
          console.warn(
            "config.json contains placeholder values, falling back to environment variables",
          );
          response = {
            ...loadConfigFromEnv(),
            version,
          };
        } else {
          // Transform the config to ensure proper structure
          // Convert string values to arrays where needed
          // Parse environment - check for placeholder or valid value
          const rawEnv = rawConfig.environment;
          const environment =
            rawEnv && !String(rawEnv).includes("#{")
              ? normalizeEnvironment(String(rawEnv))
              : undefined;

          response = {
            ...rawConfig,
            auth: {
              clientId: rawConfig.auth?.clientId || "",
              authority: rawConfig.auth?.authority || "",
              // Handle scopes - split if string, keep as array if already array
              scopes: Array.isArray(rawConfig.auth?.scopes)
                ? rawConfig.auth.scopes
                : rawConfig.auth?.scopes
                  ? typeof rawConfig.auth.scopes === "string" &&
                    rawConfig.auth.scopes.includes(",")
                    ? rawConfig.auth.scopes
                        .split(",")
                        .map((s: string) => s.trim())
                    : [rawConfig.auth.scopes.trim()]
                  : [],
              // Handle knownAuthorities - split if string, keep as array if already array
              knownAuthorities: Array.isArray(rawConfig.auth?.knownAuthorities)
                ? rawConfig.auth.knownAuthorities
                : rawConfig.auth?.knownAuthorities
                  ? typeof rawConfig.auth.knownAuthorities === "string" &&
                    rawConfig.auth.knownAuthorities.includes(",")
                    ? rawConfig.auth.knownAuthorities
                        .split(",")
                        .map((s: string) => s.trim())
                    : [rawConfig.auth.knownAuthorities.trim()]
                  : [],
              redirectUri:
                rawConfig.auth?.redirectUri || window.location.origin,
              postLogoutRedirectUri:
                rawConfig.auth?.postLogoutRedirectUri || "",
            },
            environment,
            version,
          };
        }
      } catch (fetchError) {
        console.warn(
          "Failed to load config.json, falling back to environment variables:",
          fetchError,
        );
        response = loadConfigFromEnv();
      }
    }
  } catch (error) {
    console.error("An error occurred reading application config: ", error);
    // Last resort: try to load from env
    if (!response) {
      console.warn(
        "Attempting to load configuration from environment variables as fallback",
      );
      response = loadConfigFromEnv();
    }
    // If we still don't have a response, throw
    if (!response) {
      throw new Error(
        "Failed to load configuration from both config.json and environment variables",
      );
    }
  }

  if (!response) {
    throw new Error("Failed to load configuration");
  }
  return response;
};

export interface MSALConfig {
  msalInstance: PublicClientApplication;
  config: AppConfig;
}

let configData: MSALConfig | null = null;

const getMsalInstance = async (): Promise<MSALConfig> => {
  try {
    if (!configData) {
      const config = await getConfig();
      msalConfig.auth.clientId = config.auth.clientId;
      msalConfig.auth.authority = config.auth.authority;
      msalConfig.auth.knownAuthorities = config.auth.knownAuthorities;
      msalConfig.auth.redirectUri = config.auth.redirectUri;
      msalConfig.auth.postLogoutRedirectUri = config.auth.postLogoutRedirectUri;
      const publicClientApplication = new PublicClientApplication(msalConfig);
      configData = {
        config: config,
        msalInstance: publicClientApplication,
      };
    }
  } catch (error) {
    console.error(`Error in creating msalInstance ${error}`);
    throw error;
  }
  return configData;
};

const ConfigService = {
  getConfig,
  getEnvValue,
  getMsalInstance,
};

export default ConfigService;
